import { eWicBalanceInquiry } from 'src/balance-inquiry/services/post-ewic-balance-inquiry';
import { EmeraldError, EmeraldErrorSeverity } from 'src/common/api/emerald-error';
import XLRLogger from 'src/common/helpers/logger';
import { PaymentMessage } from 'src/devices/payment/dtos/eps-data';
import { printReceipts } from 'src/devices/printer-operations';
import { giftCardBalanceInquiry } from 'src/tendering/business-logic/services/gift-card-balance-inquiry';
import { EwicBalanceInquiryDTO, EwicPrescriptionLineItem } from './handle-balance-inquiry';
import { GiftCardBalanceInquiryDto } from 'src/tendering/interfaces/gift-card-balance-inquiry-dto';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { ReceiptData } from 'src/devices/payment/dtos/eps-data';
import { checkAndGetArray } from 'src/common/helpers/array-helper';
import { EwicPrintDTO } from 'src/tendering/interfaces/ewic-print.dto';
import { getEwicBalanceInquiryReceipt } from 'src/tendering/business-logic/services/get-ewic-balance-inquiry-receipt';
import { CardDetails, EpsData } from 'src/devices/payment/dtos/eps-data';
import { processDecode } from 'src/tendering/business-logic/ewic-decode-prescription';
import { IPrescriptionItem } from 'src/common/interfaces/dtos/IEwic';
import { store } from 'src/common/redux/configure-store';
import { EwicReceiptType } from 'src/tendering/enumerations/ewic-receipt-type';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { IReceipt } from 'src/common/interfaces/dtos/IReceipt';
import { CartResponse } from 'src/items-list/business-logic/service/interfaces/cart-response';

export const printEwicBalanceInquiry = async (
  ewicCardDataDTO: EwicBalanceInquiryDTO,
): Promise<boolean> => {
  return eWicBalanceInquiry(ewicCardDataDTO)
    .then((receipts: IReceipt[]) => printReceipts(receipts))
    .then(() => true)
    .catch(err => {
      XLRLogger.error({
        message: 'Balance Inquiry Failed.',
        source: 'handle-balance-inquiry-helper.ts',
      });
      popupMessage(err);
      return false;
    });
};

export const validateEwicData = (message: PaymentMessage): void => {
  if (!message.ewicData) {
    throw new EmeraldError(
      'BalanceInquiryInvalidDataCode',
      'eWIC data is undefined',
      EmeraldErrorSeverity.Error,
    );
  }
};

export const getMiddleDigits = (cardLength: number): string => {
  const firstSix = 6,
    lastFour = 4;
  if (cardLength < firstSix + lastFour) {
    return '';
  }
  return '*'.repeat(cardLength - firstSix - lastFour);
};

export const printBalanceInquiry = async (
  giftCardDataDTO: GiftCardBalanceInquiryDto,
): Promise<boolean> =>
  giftCardBalanceInquiry(giftCardDataDTO)
    .then(async (cartResponse: CartResponse) => {
      const receipts = cartResponse.receipts;
      if (receipts) {
        await printReceipts(receipts);
      }
      return true;
    })
    .catch(() => {
      XLRLogger.error({
        message: 'Balance Inquiry Failed.',
        source: 'handle-balance-inquiry-helper.ts',
      });
      return false;
    });

export const parseGiftCardBalanceInquiryReceipt = (input: ReceiptData[]): string => {
  const receiptString = checkAndGetArray(input).flatMap(
    (receiptData: ReceiptData) => receiptData.receiptLines,
  );
  const receiptReturn: string = receiptString.join(`\n`);
  return receiptReturn;
};

export const getExpirationDate = (ewicData: string): string => {
  const expirationDate = ewicData?.substring(ewicData.length - 8, ewicData.length);
  const expirationIso = `${expirationDate?.slice(0, 4)}-${expirationDate?.slice(
    4,
    6,
  )}-${expirationDate?.slice(6, 8)}`;
  return expirationIso;
};

const insertDecimal = (num: number): string => {
  return (num / 100).toFixed(2);
};

export const parsePrescriptionLineItems = (
  ewicData: string | undefined,
): EwicPrescriptionLineItem[] | undefined => {
  if (ewicData !== undefined && ewicData !== '') {
    const elements: string[] | undefined = [];
    const parsedEwicData: EwicPrescriptionLineItem[] | undefined = [];
    const ewicElementLength = 19;

    const unnecessaryDataLength = 9;
    const categoryEndIndex = 11;
    const subcategoryEndIndex = 14;
    const balanceEndIndex = 19;

    for (let i = 0, j = 0; i < ewicData.length - ewicElementLength; i += ewicElementLength) {
      elements.push(ewicData.substring(i, i + ewicElementLength));

      parsedEwicData.push({
        categoryCode: elements[j].substring(unnecessaryDataLength, categoryEndIndex),
        subcategoryCode: elements[j].substring(categoryEndIndex, subcategoryEndIndex),
        benefitQuantity: parseInt(elements[j].substring(subcategoryEndIndex, balanceEndIndex)),
        benefitQuantityDecimal: parseInt(
          insertDecimal(parseInt(elements[j].substring(subcategoryEndIndex, balanceEndIndex))),
        ),
      });
      j++;
    }
    return parsedEwicData;
  }
};

export const entryMethodStringConvert = (input: string): EntryMethod => {
  if (input === 'Swiped') {
    return EntryMethod.MSR;
  } else if (input === 'Keyed') {
    return EntryMethod.Keyed;
  } else if (input === 'Scanned') {
    return EntryMethod.Scanned;
  } else {
    return EntryMethod.Empty;
  }
};

export const printEwicBalanceInquirySE = async (
  ewicCardDataDTOForSE: EwicPrintDTO,
): Promise<boolean> =>
  getEwicBalanceInquiryReceipt(ewicCardDataDTOForSE)
    .then((receipts: IReceipt[]) => printReceipts(receipts))
    .then(() => true)
    .catch(err => {
      XLRLogger.error({
        message: 'Balance Inquiry Receipt Request Failed.',
        source: 'handle-balance-inquiry-helper.ts',
      });
      popupMessage(err);
      return false;
    });

export const handleBalanceInquiryWithEwicService = async ({
  cardDetails,
  epsData,
  transactionDateTimeUTC,
}: ProcessBalanceInquiryProps): Promise<boolean> => {
  let prescriptionItems: IPrescriptionItem[] | undefined;
  await processDecode({
    stateIdentifierCode: cardDetails.stateCode || '',
    rawPsData: epsData.ewicEpsPrescriptionData?.ewicData || '',
  }).then(response => {
    prescriptionItems = response.prescriptionItems
      .map(listItem => ({
        ...listItem,
        benefitQuantity: listItem['benefitQuantityDecimal'],
      }))
      .filter(prescriptionLine => !!prescriptionLine.unitOfMeasure);
  });

  const MIN_CARD_LENGTH = 10;
  const cardMiddleDigits = getMiddleDigits(cardDetails.cardLength ?? MIN_CARD_LENGTH);
  const ewicCardDataDTOForSE: EwicPrintDTO = {
    cardInfo: {
      stateIdentifierCode: cardDetails.stateCode || '',
      maskedPan: getMaskedPan(epsData.card, cardMiddleDigits),
      expirationDate: getExpirationDate(epsData.ewicEpsPrescriptionData?.ewicData || ''),
    },
    inquiryReferenceId: epsData.paymentTranID || '',
    utcDateTime: new Date().toISOString(),
    cashierName: store.getState().user.displayName,
    cashierUsername: store.getState().user.userName,
    receiptType: EwicReceiptType.BALANCEINQUIRY,
    itemPrescriptions: prescriptionItems || [],
    authorizationCodeNumber: epsData?.authCode || '',
    authorizationUTCDateTime: transactionDateTimeUTC || new Date().toISOString(),
  };

  return await printEwicBalanceInquirySE(ewicCardDataDTOForSE);
};

export const handleBalanceInquiryWithTransactionService = async ({
  cardDetails,
  epsData,
}: ProcessBalanceInquiryProps): Promise<boolean> => {
  const MIN_CARD_LENGTH = 10;
  const cardMiddleDigits = getMiddleDigits(cardDetails.cardLength ?? MIN_CARD_LENGTH);
  const ewicCardDataDTO: EwicBalanceInquiryDTO = {
    stateIdentifierCode: cardDetails.stateCode || '',
    pan: getMaskedPan(epsData.card, cardMiddleDigits),
    expirationDate: getExpirationDate(epsData.ewicEpsPrescriptionData?.ewicData || ''),
    authorizationCode: epsData?.authCode || '',
    isSaleMode: false,
    prescriptionLineItems: parsePrescriptionLineItems(epsData.ewicEpsPrescriptionData?.ewicData),
    referenceNumber: epsData.paymentTranID || '',
  };

  return await printEwicBalanceInquiry(ewicCardDataDTO);
};

const getMaskedPan = (cardDetails: CardDetails, cardMiddleDigits: string): string => {
  return `${cardDetails.firstSix}${cardMiddleDigits}${cardDetails.lastFour}`;
};

interface ProcessBalanceInquiryProps {
  cardDetails: CardDetails;
  epsData: EpsData;
  transactionDateTimeUTC?: string;
}
